import { assertBasisPoints, assertPaise, mulDivRound } from "./integer";

/**
 * GST on one tax rate group, split into CGST and SGST (IGST is never used:
 * restaurant service is supplied where the restaurant is).
 *
 * CGST and SGST are each half the rate. One half is computed and rounded, and
 * used for both, so the two are always equal and tax = 2 × half.
 */
export interface GroupTax {
	taxablePaise: number;
	/** Equal to `sgstPaise`, always. */
	cgstPaise: number;
	sgstPaise: number;
}

/**
 * Prices exclusive of tax: the tax is added on top of the value.
 *
 *   half    = round(taxable × rate ÷ 20000)
 *   tax     = 2 × half
 *
 * Example: taxable ₹10.10 at 5%: half = round(25.25) = 25 paise, tax = 50 paise.
 */
export function taxOnExclusive(taxablePaise: number, rateBp: number): GroupTax {
	assertPaise(taxablePaise, "taxable value");
	assertBasisPoints(rateBp, "tax rate");
	const half = mulDivRound(taxablePaise, rateBp, 20000);
	return { taxablePaise, cgstPaise: half, sgstPaise: half };
}

/**
 * Prices inclusive of tax: the tax is taken out of the value, which stays what
 * the diner pays.
 *
 *   half    = round(gross × rate ÷ (2 × (10000 + rate)))
 *   tax     = 2 × half
 *   taxable = gross − tax
 *
 * Example: gross ₹105 at 5%: half = round(250) = ₹2.50, taxable = ₹100.
 * Example: gross ₹100 at 5%: half = round(238.1) = ₹2.38, tax = ₹4.76, taxable = ₹95.24.
 */
export function taxInInclusive(grossPaise: number, rateBp: number): GroupTax {
	assertPaise(grossPaise, "value including tax");
	assertBasisPoints(rateBp, "tax rate");
	const half = mulDivRound(grossPaise, rateBp, 2 * (10000 + rateBp));
	return { taxablePaise: grossPaise - 2 * half, cgstPaise: half, sgstPaise: half };
}
